import { queryOptions } from '@tanstack/react-query';
import { Workorder } from '@certhon/models';
import {
  fetchDayWorkorders,
  fetchRoutes,
  fetchAllDayWorkorders,
  fetchWeekWorkorders,
  fetchMonthWorkorders,
} from './dayplanning.api';
import { geocodeWorkorderAddresses } from '../workorders-map.api';
import { buildRoutePairs, HQ_LOCATION, QUERY_CONFIG } from '../shared';

export const dayPlanningQueries = {
  all: () => ['dayplanning'] as const,

  day: (userId: number, date: string) =>
    queryOptions({
      queryKey: [...dayPlanningQueries.all(), 'day', userId, date] as const,
      queryFn: () => fetchDayWorkorders(userId, date),
      staleTime: QUERY_CONFIG.dayWorkordersStale,
    }),

  week: (userId: number, weekStart: string) =>
    queryOptions({
      queryKey: [
        ...dayPlanningQueries.all(),
        'week',
        userId,
        weekStart,
      ] as const,
      queryFn: () => fetchWeekWorkorders(userId, weekStart),
      staleTime: QUERY_CONFIG.dayWorkordersStale,
    }),

  month: (userId: number, monthStart: string) =>
    queryOptions({
      queryKey: [
        ...dayPlanningQueries.all(),
        'month',
        userId,
        monthStart,
      ] as const,
      queryFn: () => fetchMonthWorkorders(userId, monthStart),
      staleTime: QUERY_CONFIG.dayWorkordersStale,
    }),

  allTechnicians: (date: string) =>
    queryOptions({
      queryKey: [...dayPlanningQueries.all(), 'all-techs', date] as const,
      queryFn: () => fetchAllDayWorkorders(date),
      staleTime: QUERY_CONFIG.allTechniciansStale,
    }),

  routes: (workorders: Workorder[]) =>
    queryOptions({
      queryKey: [
        ...dayPlanningQueries.all(),
        'routes',
        // Include recnum, route_option, and sort_order to refetch when any change
        workorders
          .map((w) => `${w.recnum}:${w.route_option ?? 'direct'}`)
          .join(','),
      ] as const,
      queryFn: async () => {
        const routePairs = buildRoutePairs(workorders, HQ_LOCATION.address);
        const results = await fetchRoutes(routePairs);

        // Failed routes are included in results with error messages
        // UI handles displaying these errors to users
        return results;
      },
      staleTime: QUERY_CONFIG.routesStale,
      enabled: workorders.length > 0,
      // Don't retry on failure - address issues won't fix themselves
      retry: false,
    }),

  /**
   * Routes for a specific technician (same as regular routes query but namespaced)
   * This allows individual technician routes to be cached separately and reused
   * when switching between "All Technicians" view and individual tech tabs
   */
  techRoutes: (techId: number, workorders: Workorder[]) =>
    queryOptions({
      queryKey: [
        ...dayPlanningQueries.all(),
        'tech-routes',
        techId,
        workorders
          .map((w) => `${w.recnum}:${w.route_option ?? 'direct'}`)
          .join(','),
      ] as const,
      queryFn: async () => {
        const routePairs = buildRoutePairs(workorders, HQ_LOCATION.address);
        const results = await fetchRoutes(routePairs);

        // Failed routes are included in results with error messages
        // UI handles displaying these errors to users
        return results;
      },
      staleTime: QUERY_CONFIG.routesStale,
      enabled: workorders.length > 0,
      // Don't retry on failure - address issues won't fix themselves
      retry: false,
    }),

  /**
   * Geocode workorder addresses as fallback for when routing fails
   * This ensures workorders appear on the map even if route calculation fails
   */
  geocode: (workorders: Workorder[]) =>
    queryOptions({
      queryKey: [
        ...dayPlanningQueries.all(),
        'geocode',
        workorders
          .map((w) => `${w.recnum}:${w.location_address ?? ''}`)
          .join(','),
      ] as const,
      queryFn: () => geocodeWorkorderAddresses(workorders),
      staleTime: QUERY_CONFIG.geocodeStale,
      enabled: workorders.length > 0,
    }),
};
